/**
 * Remember which conversations were open across a reload.
 *
 * An agent mid-conversation who refreshes, or whose session reloads after a deploy, used
 * to lose every open window and have to hunt for the thread again. The dock is restored
 * instead — the same behaviour a desktop chat client has.
 *
 * sessionStorage, not localStorage, deliberately: windows belong to THIS tab's session.
 * Two tabs should not fight over one dock, and a shared machine should not show the last
 * agent's conversations to the next one.
 *
 * Nothing read back is trusted. Stored JSON can be stale, truncated, hand-edited, or from
 * an older shape of the app, so every field is validated and anything odd is discarded —
 * a corrupt dock must never stop the chat from loading.
 */

const KEY = 'daksha.chatDock.v1';
/** Keep the stored payload small: it is read on every page load. */
const MAX_PERSISTED = 12;

/**
 * The only fields worth keeping. A whole chat object would store stale names and photos.
 *
 * ⚠️ `photo` is the field the app reads everywhere (ChatList rows, the bubble, the
 * header). An earlier version of this stored `avatar`, so every restored conversation
 * came back with initials instead of its picture.
 */
const TYPES = new Set(['group', 'channel']);
const slim = (c) => ({
    id: String(c.id),
    name: String(c.name || '').slice(0, 80),
    // A channel must come back as a channel: restored as a "group" it lost its posting
    // rules, its notices and its details panel.
    type: TYPES.has(c.type) ? c.type : 'direct',
    ...(c.type === 'channel' ? { isChannel: true, isGroup: true, chatId: String(c.id) } : {}),
    ...(c.type === 'channel' && typeof c.kind === 'string' ? { kind: c.kind.slice(0, 20) } : {}),
    photo: typeof c.photo === 'string' ? c.photo.slice(0, 500) : '',
    targetUserId: typeof c.targetUserId === 'string' ? c.targetUserId.slice(0, 200) : '',
});

const isChat = (c) => !!c && typeof c === 'object' && typeof c.id === 'string' && c.id.length > 0 && c.id.length < 200;

/** Drop duplicates, keeping the first — the dock's invariant is one entry per chat. */
const dedupe = (list) => {
    const seen = new Set();
    const out = [];
    for (const c of list) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        out.push(c);
    }
    return out;
};

/** @returns {{activeChats: object[], minimizedChats: object[]}|null} */
export function loadDock() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const active = dedupe((Array.isArray(parsed.activeChats) ? parsed.activeChats : []).filter(isChat).map(slim));
        const minimized = dedupe((Array.isArray(parsed.minimizedChats) ? parsed.minimizedChats : []).filter(isChat).map(slim));
        // A chat cannot be in both lists — if a stored dock says otherwise, the open
        // window wins and the stray bubble is dropped.
        const activeIds = new Set(active.map(c => c.id));
        const cleanMin = minimized.filter(c => !activeIds.has(c.id));
        if (!active.length && !cleanMin.length) return null;
        return {
            activeChats: active.slice(0, MAX_PERSISTED),
            minimizedChats: cleanMin.slice(0, MAX_PERSISTED),
        };
    } catch {
        return null;            // private mode, blocked storage, bad JSON — all the same
    }
}

export function saveDock(state) {
    try {
        const active = (state?.activeChats || []).filter(isChat).map(slim).slice(0, MAX_PERSISTED);
        const minimized = (state?.minimizedChats || []).filter(isChat).map(slim).slice(0, MAX_PERSISTED);
        if (!active.length && !minimized.length) { sessionStorage.removeItem(KEY); return; }
        sessionStorage.setItem(KEY, JSON.stringify({ activeChats: active, minimizedChats: minimized, at: Date.now() }));
    } catch { /* never let a storage failure break the chat */ }
}

export const clearDock = () => { try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } };

export const __test = { KEY, MAX_PERSISTED, slim, isChat, dedupe };
